import React, { useState } from 'react';
import { generateStoicMorning } from '../services/geminiService';
import { StoicMorningGuide } from '../types';
import { Sunrise, RefreshCw, Shield, Target } from 'lucide-react';
import { useUser } from '../context/UserContext';

const StoicMorning: React.FC = () => {
  const [plans, setPlans] = useState('');
  const [guide, setGuide] = useState<StoicMorningGuide | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp } = useUser();

  const handlePrepare = async () => {
    if (!plans.trim()) return;
    setLoading(true);
    setGuide(null);
    try {
      const data = await generateStoicMorning(plans);
      setGuide(data);
      addXp(50, 'Completed Morning Prep');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-amber-50 text-amber-600 rounded-full mb-4">
          <Sunrise size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">Morning Rehearsal</h1>
        <p className="text-nunchi-600">
          "When you wake up in the morning, tell yourself: The people I deal with today will be meddling, ungrateful, arrogant..." <br/>
          Anticipate adversity to meet it with Areté.
        </p>
      </header>

      {!guide && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                What is your agenda for today?
            </label>
            <textarea
              value={plans}
              onChange={(e) => setPlans(e.target.value)}
              placeholder="e.g., Commute to work, Client meeting at 10am, Gym in the evening, Dinner with family..."
              className="w-full h-32 bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-amber-200 outline-none resize-none text-nunchi-800 placeholder:text-nunchi-300 mb-4"
            />
            <button
              onClick={handlePrepare}
              disabled={!plans.trim()}
              className="w-full bg-amber-700 text-white py-4 rounded-xl font-bold hover:bg-amber-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Sunrise size={18} /> Prepare My Mind
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="w-16 h-16 border-4 border-amber-100 border-t-amber-600 rounded-full animate-spin mx-auto mb-4"></div>
             <p className="font-serif italic text-amber-700">Forecasting the storms...</p>
        </div>
      )}

      {guide && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-white border border-nunchi-200 rounded-2xl p-8 shadow-xl relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-amber-50 opacity-50 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <div className="mb-8">
                     <h3 className="text-amber-600 uppercase tracking-widest text-xs font-bold mb-3 flex items-center gap-2">
                         <Shield size={14} /> The Forecast (Premeditatio Malorum)
                     </h3>
                     <p className="text-lg leading-relaxed font-serif text-nunchi-800 border-l-4 border-amber-400 pl-4 bg-amber-50/50 py-2">
                         {guide.forecast}
                     </p>
                 </div>

                 <div className="mb-8">
                     <h3 className="text-nunchi-400 uppercase tracking-widest text-xs font-bold mb-3">
                         Required Virtues
                     </h3>
                     <div className="flex flex-wrap gap-2">
                         {guide.virtues.map((v, i) => (
                             <span key={i} className="px-3 py-1 bg-stone-100 text-stone-700 rounded-full text-sm font-bold border border-stone-200">
                                 {v}
                             </span>
                         ))}
                     </div>
                 </div>

                 <div className="text-center p-6 bg-stone-800 text-stone-100 rounded-xl">
                     <Target className="mx-auto mb-2 text-amber-400" size={24} />
                     <p className="text-xs text-stone-400 uppercase font-bold mb-2">Daily Intention</p>
                     <p className="text-xl font-bold font-serif">"{guide.intention}"</p>
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setGuide(null); setPlans(''); }}
                  className="text-amber-700 hover:text-amber-900 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Rehearse Another Day
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default StoicMorning;